// src/pages/PersonProfile.js
// Full profile page for any person, served under both people pages
// (/individuals/:id and /rga-partners/:id; the back link follows the route): who
// they are, the mandates they sit on as an investor, and, for RGA Partners
// or anyone with rows there, their partner pipeline, the investors they
// advise on and portal access. Plus deals introduced, events and activity.
// Data comes from GET /api/contacts/:id/profile.
import { useState } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { firmsOf } from '../lib/contactFirms'
import { isRgaPartner } from '../lib/roles'
import { PARTNER_BADGE } from '../lib/partnerPipelineStages'
import { PL_BADGE } from '../lib/pipelineStages'
import { PeekLink } from '../components/Peek'
import EventsAttended from '../components/EventsAttended'
import VenturesLinked from '../components/VenturesLinked'
import ActivityTimeline from '../components/ActivityTimeline'
import { CONTACT_TYPE_BADGE, invalidatePeople, Modal, Field, DeleteConfirmModal, tagColor } from '../components/people/ui'
import PersonForm from '../components/people/PersonForm'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function SectionTitle({ children, count }) {
  return (
    <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
      {children}
      {count != null && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>{count}</span>}
    </h2>
  )
}

function Empty({ children }) {
  return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{children}</div>
}

// Bordered table with a header row; columns as a CSS grid template.
function Grid({ columns, headers, rows }) {
  const cell = { display: 'grid', gridTemplateColumns: columns, gap: 12, padding: '9px 14px', alignItems: 'center' }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{
        ...cell, padding: '7px 14px', background: 'var(--surface-raised)',
        borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)',
      }}>
        {headers.map(h => <span key={h}>{h}</span>)}
      </div>
      {rows.map((r, i) => (
        <div key={r.key} style={{
          ...cell, fontSize: 13,
          borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          {r.cells}
        </div>
      ))}
    </div>
  )
}

const mandateLink = m => m
  ? <Link to={`/mandates/${m.mandate_id}`} style={{ color: 'var(--accent)', fontWeight: 500 }}>{m.mandate_name}</Link>
  : '—'

const SUBMISSION_BADGE = { pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red' }
// A name RGA does not approve shows as Not approved (Travis, 10/29).
const SUBMISSION_LABEL = { pending: 'pending', approved: 'approved', rejected: 'not approved' }
const DEAL_BADGE = { pending: 'badge-yellow', taken_on: 'badge-green', not_proceeding: 'badge-red' }
const DEAL_LABEL = { pending: 'Pending', taken_on: 'Taken on', not_proceeding: 'Not proceeding' }

// Summary tiles for the Partner Record card.
function PartnerRecord({ dealsIntroduced, dealsSubmitted, mandates, names, advised }) {
  const count = (rows, f) => rows.filter(f).length
  const tiles = [
    ['Deals introduced', dealsIntroduced],
    ['Deals submitted', dealsSubmitted.length,
      dealsSubmitted.length ? `${count(dealsSubmitted, d => d.status === 'taken_on')} taken on` : null],
    ['Mandates partnered', mandates],
    ['Names submitted', names.length,
      names.length ? `${count(names, n => n.status === 'approved')} approved · ${count(names, n => n.status === 'pending')} pending · ${count(names, n => n.status === 'rejected')} not approved` : null],
    ['Investors advised', advised.length,
      advised.length ? `${count(advised, a => ['In Diligence', 'In Documentation', 'Closed'].includes(a.stage))} in diligence or later` : null],
  ]
  const byType = Object.entries(names.reduce((m, n) => ({ ...m, [n.name_type]: (m[n.name_type] ?? 0) + 1 }), {}))
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
        {tiles.map(([label, value, sub]) => (
          <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
          </div>
        ))}
      </div>
      {byType.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>
          Names by type: {byType.map(([t, n]) => `${t} ${n}`).join(' · ')}
        </div>
      )}
    </>
  )
}

// Which people page this profile was opened under, for the back link.
const PAGES = {
  'rga-partners': { path: '/rga-partners', label: 'RGA Partners' },
  individuals:    { path: '/individuals',  label: 'Individuals' },
}

export default function PersonProfile() {
  const { id } = useParams()
  const page = PAGES[useLocation().pathname.split('/')[1]] ?? PAGES.individuals
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['contactProfile', id],
    queryFn: () => api.get(`/api/contacts/${id}/profile`).then(r => r.data.data),
  })

  // Only needed by the edit form.
  const { data: firms = [] } = useQuery({
    queryKey: ['firms'], enabled: editing,
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'], enabled: editing,
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: users = [] } = useQuery({
    queryKey: ['relationship-owners'], enabled: editing,
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
  })
  const { data: tagSuggestions = [] } = useQuery({
    queryKey: ['contactTags'], enabled: editing,
    queryFn: () => api.get('/api/contacts/tags').then(r => r.data.data ?? []),
  })

  // Back to the list this profile was opened from; the profile itself is
  // dropped from the cache rather than refetched into a 404.
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/contacts/${id}`).then(r => r.data),
    onSuccess: () => {
      navigate(page.path, { replace: true })
      qc.removeQueries({ queryKey: ['contactProfile', id] })
      invalidatePeople(qc)
    },
  })

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/contacts/${id}`, payload).then(r => r.data),
    onSuccess: () => {
      invalidatePeople(qc, id)
      setEditing(false)
    },
  })

  const back = (
    <div style={{ marginBottom: 16 }}>
      <Link to={page.path} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>← {page.label}</Link>
    </div>
  )

  if (isLoading) {
    return (
      <div className="page">
        {back}
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}>
          <div className="spinner spinner-lg" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="page">
        {back}
        <div className="card" style={{ color: 'var(--danger)' }}>
          {error?.response?.status === 404 ? 'Person not found.' : `Could not load this person: ${error?.message}`}
        </div>
      </div>
    )
  }

  const { contact: p, partner_pipeline, advised, portal_mandates, deals_introduced } = data
  const names_submitted = data.names_submitted ?? []
  const deals_submitted = data.deals_submitted ?? []
  const submitted_about = data.submitted_about ?? []
  const rejectedFor = submitted_about.filter(x => x.status === 'rejected')
  const investorPipeline = data.investor_pipeline ?? []
  const advisedMandates = new Set(advised.map(a => a.mandate?.mandate_id)).size
  // Partner sections show for RGA Partners, or anyone who has rows in them
  const partner = isRgaPartner(p)

  return (
    <div className="page">
      {back}

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>{p.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {/* Each firm with the title there (migrations/076); a title with no firm shows alone */}
              {!firmsOf(p).length && p.title_role && <div>{p.title_role}</div>}
              {firmsOf(p).map(f => (
                <div key={f.firm_id}>
                  {f.title_role && <span>{f.title_role}, </span>}
                  <PeekLink type="firm" id={f.firm_id}>{f.name}</PeekLink>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {(p.contact_type ?? []).map(t => (
                <span key={t} className={`badge ${CONTACT_TYPE_BADGE[t] ?? 'badge-gray'}`}>{t}</span>
              ))}
              {partner && <>
                <span className={`badge ${p.partnership_agreement ? 'badge-green' : 'badge-gray'}`}>
                  {p.partnership_agreement ? '✓ Partnership Agreement' : '✗ No Agreement'}
                </span>
                <span className={`badge ${p.is_licensed ? 'badge-green' : 'badge-gray'}`}>
                  {p.is_licensed ? '✓ Licensed' : '✗ Not Licensed'}
                </span>
              </>}
              {(p.tags ?? []).map(t => (
                <span key={t} className={`badge ${tagColor(t)}`}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => { deleteMutation.reset(); setConfirmDelete(true) }}>Delete</button>
          </div>
        </div>

        <div className="divider" style={{ margin: '18px 0' }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px 24px' }}>
          <Field label="Email">
            {(p.email ?? []).map(e => (
              <div key={e}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>
            ))}
          </Field>
          <Field label="Phone">
            {(p.phone ?? []).map(ph => <div key={ph}>{ph}</div>)}
          </Field>
          <Field label="Relationship Owner">
            {p.owners.map(o => <div key={o.id}>{o.name}</div>)}
          </Field>
          <Field label="Introduced By">
            {p.introducer && <PeekLink type="contact" id={p.introducer.contact_id}>{p.introducer.name}</PeekLink>}
          </Field>
          <Field label="Last Interaction">{p.last_interaction && formatDate(p.last_interaction)}</Field>
          <Field label="Days Since Contact">
            {p.days_since_contact != null && (
              <span style={{ color: p.days_since_contact > 30 ? 'var(--danger)' : 'var(--text-primary)' }}>
                {p.days_since_contact}d
              </span>
            )}
          </Field>
        </div>

        {p.notes && (
          <>
            <div className="divider" style={{ margin: '18px 0' }} />
            <Field label="Notes">
              <div style={{ whiteSpace: 'pre-wrap' }}>{p.notes}</div>
            </Field>
          </>
        )}
      </div>

      {/* Partner submissions of this person: names not approved, flagged for RGA */}
      {rejectedFor.length > 0 && (
        <div className="card" style={{ marginBottom: 20, border: '1px solid rgba(239,68,68,0.35)', background: 'var(--danger-muted)' }}>
          {rejectedFor.map(x => (
            <div key={x.submission_id} style={{ fontSize: 13, marginBottom: 2 }}>
              <strong style={{ fontWeight: 600 }}>Not approved for {x.mandate?.mandate_name ?? 'a mandate'}</strong>
              {': submitted by '}{x.partner?.name ?? 'a partner'}{', '}{formatDate(x.created_at)}
              {x.review_notes && <span style={{ color: 'var(--text-secondary)' }}> · {x.review_notes}</span>}
            </div>
          ))}
        </div>
      )}
      {submitted_about.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={submitted_about.length}>Submitted by Partners</SectionTitle>
          <Grid
            columns="1.4fr 1.4fr 1.2fr 110px 110px"
            headers={['Mandate', 'Submitted by', 'Type', 'Submitted', 'Status']}
            rows={submitted_about.map(x => ({
              key: x.submission_id,
              cells: <>
                <span>{mandateLink(x.mandate)}</span>
                <span>{x.partner ? <PeekLink type="contact" id={x.partner.contact_id}>{x.partner.name}</PeekLink> : '—'}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{x.name_type}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{formatDate(x.created_at)}</span>
                <span><span className={`badge ${SUBMISSION_BADGE[x.status] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{SUBMISSION_LABEL[x.status] ?? x.status}</span></span>
              </>,
            }))}
          />
        </div>
      )}

      {/* Mandates this person is on as the investor contact */}
      {investorPipeline.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={investorPipeline.length}>Investor Pipeline</SectionTitle>
          <Grid
            columns="1.5fr 1.5fr 1.5fr 180px"
            headers={['Mandate', 'Investor firm', 'RGA Partner', 'Stage']}
            rows={investorPipeline.map(r => ({
              key: r.pipeline_id,
              cells: <>
                <span>{mandateLink(r.mandate)}</span>
                <span>{r.investor_firm
                  ? <PeekLink type="firm" id={r.investor_firm.firm_id}>{r.investor_firm.name}</PeekLink>
                  : '—'}</span>
                <span>{r.partner_advisor
                  ? <PeekLink type="contact" id={r.partner_advisor.contact_id}>{r.partner_advisor.name}</PeekLink>
                  : '—'}</span>
                <span><span className={`badge ${PL_BADGE[r.stage] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{r.stage}</span></span>
              </>,
            }))}
          />
        </div>
      )}

      {/* Partner record (item 9): everything this partner has contributed,
          summarized; the sections below have the detail. */}
      {(partner || names_submitted.length > 0 || deals_submitted.length > 0) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle>Partner Record</SectionTitle>
          <PartnerRecord
            dealsIntroduced={deals_introduced.length}
            dealsSubmitted={deals_submitted}
            mandates={partner_pipeline.length}
            names={names_submitted}
            advised={advised}
          />
        </div>
      )}

      {/* Partner pipeline across mandates */}
      {(partner || partner_pipeline.length > 0) && (
      <div className="card" style={{ marginBottom: 20 }}>
        <SectionTitle count={partner_pipeline.length}>Mandates</SectionTitle>
        {partner_pipeline.length === 0 ? (
          <Empty>Not in any mandate's partner pipeline yet.</Empty>
        ) : (
          <Grid
            columns="2fr 140px 180px 1fr 110px"
            headers={['Mandate', 'Stage', 'Agreement', 'Mandate status', 'Updated']}
            rows={partner_pipeline.map(r => ({
              key: r.id,
              cells: <>
                <span>{mandateLink(r.mandate)}</span>
                <span><span className={`badge ${PARTNER_BADGE[r.stage] ?? 'badge-gray'}`} style={{ fontSize: 11 }}>{r.stage}</span></span>
                <span>{r.stage !== 'Partnered'
                  ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                  : <span className={`badge ${r.partnership_agreement_on_file ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: 11 }}>
                      {r.partnership_agreement_on_file ? 'Agreement on file' : 'No partnership agreement'}
                    </span>}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{r.mandate?.status ?? '—'}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{formatDate(r.updated_at)}</span>
              </>,
            }))}
          />
        )}
      </div>
      )}

      {/* Investors this partner advises on */}
      {(partner || advised.length > 0) && (
      <div className="card" style={{ marginBottom: 20 }}>
        <SectionTitle count={advised.length}>
          Investors Advised{advisedMandates > 1 ? ` across ${advisedMandates} mandates` : ''}
        </SectionTitle>
        {advised.length === 0 ? (
          <Empty>Not the partner advisor on any investor yet.</Empty>
        ) : (
          <Grid
            columns="1.5fr 1.5fr 1.5fr 180px"
            headers={['Mandate', 'Investor firm', 'Investor contact', 'Stage']}
            rows={advised.map(a => ({
              key: a.pipeline_id,
              cells: <>
                <span>{mandateLink(a.mandate)}</span>
                <span>{a.investor_firm
                  ? <PeekLink type="firm" id={a.investor_firm.firm_id}>{a.investor_firm.name}</PeekLink>
                  : '—'}</span>
                <span>{a.investor_contact
                  ? <PeekLink type="contact" id={a.investor_contact.contact_id}>{a.investor_contact.name}</PeekLink>
                  : '—'}</span>
                <span><span className={`badge ${PL_BADGE[a.stage] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{a.stage}</span></span>
              </>,
            }))}
          />
        )}
      </div>
      )}

      {/* Mandates this person brought to RGA */}
      {deals_introduced.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={deals_introduced.length}>Deals Introduced</SectionTitle>
          <Grid
            columns="2fr 1fr 1fr"
            headers={['Mandate', 'BD stage', 'Status']}
            rows={deals_introduced.map(m => ({
              key: m.mandate_id,
              cells: <>
                <span>{mandateLink(m)}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{m.bd_stage ?? '—'}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{m.status ?? '—'}</span>
              </>,
            }))}
          />
        </div>
      )}

      {/* Portal submissions: names (every status; RGA sees rejections and
          its own review notes) and deals */}
      {names_submitted.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={names_submitted.length}>Names Submitted</SectionTitle>
          <Grid
            columns="1.4fr 1.4fr 1.2fr 1.2fr 110px 1.4fr"
            headers={['Name', 'Firm', 'Type', 'Mandate', 'Status', 'Review note']}
            rows={names_submitted.map(n => ({
              key: n.submission_id,
              cells: <>
                <span style={{ fontWeight: 500 }}>{n.contact_name}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{n.firm_name ?? '—'}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{n.name_type}</span>
                <span>{mandateLink(n.mandate)}</span>
                <span><span className={`badge ${SUBMISSION_BADGE[n.status] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{SUBMISSION_LABEL[n.status] ?? n.status}</span></span>
                <span style={{ color: 'var(--text-secondary)' }}>{n.review_notes ?? ''}</span>
              </>,
            }))}
          />
        </div>
      )}

      {deals_submitted.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={deals_submitted.length}>Deals Submitted</SectionTitle>
          <Grid
            columns="1.6fr 1.6fr 110px 120px 1.4fr"
            headers={['Company or fund', 'Work type', 'Submitted', 'Status', 'Review note']}
            rows={deals_submitted.map(d => ({
              key: d.deal_submission_id,
              cells: <>
                <span style={{ fontWeight: 500 }}>{d.company_name}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{[d.work_type, d.work_subtype, d.deal_kind].filter(Boolean).join(' · ')}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{formatDate(d.created_at)}</span>
                <span><span className={`badge ${DEAL_BADGE[d.status] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{DEAL_LABEL[d.status] ?? d.status}</span></span>
                <span style={{ color: 'var(--text-secondary)' }}>{d.review_notes ?? ''}</span>
              </>,
            }))}
          />
        </div>
      )}

      {/* Partner portal access */}
      {portal_mandates.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle count={portal_mandates.length}>Partner Portal Access</SectionTitle>
          <Grid
            columns="2fr 1fr"
            headers={['Mandate', 'Engaged since']}
            rows={portal_mandates.map(m => ({
              key: m.mandate?.mandate_id,
              cells: <>
                <span>{mandateLink(m.mandate)}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{formatDate(m.engaged_at)}</span>
              </>,
            }))}
          />
        </div>
      )}

      {/* Events + activity */}
      <div className="card" style={{ marginBottom: 20 }}>
        <EventsAttended contactId={id} />
        <VenturesLinked contactId={id} divider />
      </div>
      <div className="card" style={{ marginBottom: 20 }}>
        <ActivityTimeline contactId={id} emails={p.email ?? []} />
      </div>

      {confirmDelete && (
        <DeleteConfirmModal
          name={p.name}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => deleteMutation.mutate()}
          deleting={deleteMutation.isPending}
          error={deleteMutation.error && (deleteMutation.error.response?.data?.error || deleteMutation.error.message)}
        />
      )}

      {editing && (
        <Modal title={`Edit - ${p.name}`} onClose={() => setEditing(false)} width={600}>
          <PersonForm
            initial={p}
            firms={firms}
            allContacts={contacts.filter(c => c.contact_id !== p.contact_id)}
            users={users}
            tagSuggestions={tagSuggestions}
            onSubmit={updateMutation.mutateAsync}
            onCancel={() => setEditing(false)}
            submitting={updateMutation.isPending}
            error={updateMutation.error?.response?.data?.error}
          />
        </Modal>
      )}
    </div>
  )
}
